import * as React from 'react'

import { Slot } from '@radix-ui/react-slot'

import Spinner from '@/components/icons/spinner'

import { cx } from '@/libs/cva'

import { buttonVariants, type ButtonVariantProps } from './classname'

interface ButtonProps
	extends React.ComponentPropsWithoutRef<'button'>,
		ButtonVariantProps {
	isLoading?: boolean
	asChild?: boolean
}

/**
 * This component is based on the `button` element and supports all of its props
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			variant = 'primary',
			size = 'base',
			className,
			asChild = false,
			children,
			isLoading = false,
			disabled,
			...props
		}: ButtonProps,
		ref
	) => {
		const Component = asChild ? Slot : 'button'

		/**
		 * In the case of a button where asChild is true, and isLoading is true, we ensure that
		 * only on element is passed as a child to the Slot component. This is because the Slot
		 * component only accepts a single child.
		 */
		const renderInner = () => {
			if (isLoading) {
				return (
					<span className="pointer-events-none">
						<div
							className={cx(
								'absolute inset-0 flex items-center justify-center rounded-md bg-ui-bg-disabled'
							)}
						>
							<Spinner className="animate-spin" />
						</div>
						{children}
					</span>
				)
			}

			return children
		}

		return (
			<Component
				ref={ref}
				{...props}
				className={cx(buttonVariants({ variant, size }), className)}
				disabled={disabled || isLoading}
			>
				{renderInner()}
			</Component>
		)
	}
)
Button.displayName = 'Button'

export { Button }
